import { useLazyQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GetAudienceSharesDocument } from '../../../../graphql/query/audiences/GetAudienceShares.generated';
import type { GetAudiencesListQuery } from '../../../../graphql/query/audiences/GetAudiencesList.generated';
import { useShareAudience as useShareAudienceHook } from '../../hooks/useShareAudience';
import type { AudienceShares } from '../../types';
import { getAudienceShareFormSchema } from '../../utils/validationSchema';
import { ShareAudienceDialogContent } from '../components/ShareAudienceDialogContent';

type OpenShareModalParams = {
  audience: Pick<GetAudiencesListQuery['audience'][number], 'id' | 'name'>;
};

export const useShareAudience = () => {
  const { t } = useTranslation();
  const { shareAudience, shareLoading } = useShareAudienceHook();
  const [getAudienceShares, audienceSharesQuery] = useLazyQuery(GetAudienceSharesDocument);

  const formMethods = useForm<AudienceShares>({
    mode: 'all',
    resolver: yupResolver(getAudienceShareFormSchema(t)),
  });

  const {
    open: dialogOpen,
    close: dialogClose,
    setLoading,
    toogleConfirmationDisabled,
  } = useConfirmationDialog({
    confirmationDisabled: true,
  });

  const openShareModal = useCallback(
    ({ audience }: OpenShareModalParams) => {
      getAudienceShares({ variables: { id: audience.id } });

      const onConfirmed = formMethods.handleSubmit(async values => {
        setLoading(true);
        try {
          await shareAudience(values, audience.id, audienceSharesQuery.data?.audienceSharesByAudience ?? []);
        } finally {
          setLoading(false);
          dialogClose();
        }
      });

      dialogOpen({
        confirmationButtonColor: 'primary',
        confirmationLabel: t('Share'),
        content: (
          <FormProvider {...formMethods}>
            <ShareAudienceDialogContent audience={audience} toogleConfirmationDisabled={toogleConfirmationDisabled} />
          </FormProvider>
        ),
        customContent: true,
        hideConfirm: !audience,
        onConfirmed,
        showExtraBtn: false,
        title: t('Share Audience'),
      });
    },
    [
      dialogOpen,
      dialogClose,
      setLoading,
      toogleConfirmationDisabled,
      t,
      shareAudience,
      formMethods,
      audienceSharesQuery,
      getAudienceShares,
    ]
  );

  return { openShareModal, isLoading: shareLoading };
};
